import { useState } from 'react';
import _ from 'lodash';

/**
 * Holds an object state whose updates merge into the previous value instead of replacing it, for components patching one field at a time. Wraps useState and pairs it with a setter that shallow-merges the patch via lodash assign.
 *
 * @param initialState - Initial state object.
 * @returns The current state and the merging setter as a tuple.
 */
const useMergeState = <S extends object>(initialState: S): [S, (newState: Partial<S>) => void] => {
  const [state, setState] = useState<S>(initialState);
  /**
   * Merges a partial patch into the current state object rather than replacing it. Calls setState with an updater that shallow-merges the previous state and the patch via lodash assign.
   *
   * @param newState - The partial state to merge in.
   * @returns Result of the underlying setState call.
   */
  const setMergedState = (newState: Partial<S>) =>
    setState((prevState) => {
      const expectedState = _.assign({}, prevState, newState);
      return expectedState;
    });
  return [state, setMergedState];
};

export default useMergeState;
